import React from 'react';
import { PieChart } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CHART_CATEGORY_PALETTE, useChartCurrency } from './chartTheme';

interface CategoryData {
  categoryName: string;
  amount: number;
  color?: string;
}

interface SpendingByCategoryPieChartProps {
  data: CategoryData[];
  title?: string;
  currencySymbol?: string;
  activeCurrencySymbol?: string;
}

export const SpendingByCategoryPieChart: React.FC<{ props: SpendingByCategoryPieChartProps; isStreaming?: boolean }> = ({ 
  props,
  isStreaming
}) => {
  const { t } = useTranslation();
  const { 
    data = [], 
    title = t('dashboard.spending_by_category'),
  } = props || {};

  const activeCurrencySymbol = useChartCurrency(props);

  // Slices need a positive amount; zero/negative/NaN entries can't be drawn.
  const validData = (Array.isArray(data) ? data : []).filter(d => d && typeof d.amount === 'number' && Number.isFinite(d.amount) && d.amount > 0);

  if (validData.length === 0) {
    if (isStreaming) {
      return (
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm text-center animate-pulse">
           <p className="text-[10px] font-black text-emerald-500 uppercase tracking-widest">{t('dashboard.calculating_breakdown')}</p>
        </div>
      );
    }
    return (
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm text-center">
         <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{t('dashboard.analytics_unavailable')}</p>
      </div>
    );
  }

  const total = validData.reduce((sum, d) => sum + d.amount, 0);
  // Assign colors once so a slice and its legend dot always match; the model's
  // color is optional and missing ones used to fall back to a near-invisible grey.
  const sortedData = [...validData]
    .sort((a, b) => b.amount - a.amount)
    .map((d, i) => ({ ...d, color: d.color || CHART_CATEGORY_PALETTE[i % CHART_CATEGORY_PALETTE.length] }));
  
  // Basic Pie Chart logic
  let currentPercentage = 0;
  const piePaths = sortedData.map((d, i) => {
    const percentage = total > 0 ? (d.amount / total) * 100 : 0;
    const startX = Math.cos(2 * Math.PI * currentPercentage / 100);
    const startY = Math.sin(2 * Math.PI * currentPercentage / 100);
    currentPercentage += percentage;
    const endX = Math.cos(2 * Math.PI * currentPercentage / 100);
    const endY = Math.sin(2 * Math.PI * currentPercentage / 100);
    const largeArcFlag = percentage > 50 ? 1 : 0;
    
    // Path for SVG circle segment
    return {
      path: `M 0 0 L ${startX} ${startY} A 1 1 0 ${largeArcFlag} 1 ${endX} ${endY} Z`,
      color: d.color,
      name: d.categoryName || t('common.other', { defaultValue: 'Other' }),
      percent: percentage
    };
  });

  return (
    <div className="bg-white rounded-[2rem] border border-gray-100 shadow-xl overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="px-4 py-4 sm:px-5 bg-[#1a2b3c] flex flex-wrap gap-3 justify-between items-center">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-emerald-500 rounded-lg text-white">
            <PieChart size={14} />
          </div>
          <span className="text-[10px] font-black text-white uppercase tracking-widest">{title}</span>
        </div>
        <div className="text-right">
          <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest block opacity-60">{t('dashboard.total_overview')}</span>
          <span className="text-sm font-black text-emerald-400">{activeCurrencySymbol}{total.toFixed(2)}</span>
        </div>
      </div>

      <div className="p-3 sm:p-4 flex flex-col items-center gap-4 bg-white">
        {sortedData.length <= 6 && <div className="relative w-40 h-40 shrink-0">
          <svg viewBox="-1.1 -1.1 2.2 2.2" className="w-full h-full -rotate-90">
            {/* A single 100% slice is a zero-length arc in SVG, so draw it as a full circle. */}
            {piePaths.length === 1 ? (
              <circle cx="0" cy="0" r="1" fill={piePaths[0].color} />
            ) : piePaths.map((p, i) => (
              <path key={i} d={p.path} fill={p.color} className="hover:opacity-80 transition-opacity cursor-pointer" />
            ))}
            <circle cx="0" cy="0" r="0.65" fill="white" />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center pointer-events-none">
            <p className="text-[8px] font-black text-gray-400 uppercase tracking-tighter leading-none mb-0.5">{t('dashboard.summary')}</p>
            <p className="text-[12px] font-black text-[#1a2b3c] leading-none">{activeCurrencySymbol}{total.toFixed(0)}</p>
          </div>
        </div>}

        <div role="region" aria-label={title} tabIndex={0} className="max-h-80 overflow-y-auto overscroll-contain space-y-3 w-full min-w-0 pr-1 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500">
          {sortedData.map((d, i) => (
            <div key={`${d.categoryName}-${i}`} className="space-y-1.5">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 text-xs">
                <div className="flex min-w-0 flex-1 items-start gap-2">
                  <span className="mt-1 size-2 shrink-0 rounded-full" style={{ backgroundColor: d.color }} aria-hidden="true" />
                  <span className="min-w-0 break-words font-bold text-[#1a2b3c]">{d.categoryName || t('common.other', { defaultValue: 'Other' })}</span>
                </div>
                <span className="font-mono text-[#1a2b3c]">
                  {activeCurrencySymbol}{d.amount.toFixed(2)} <span className="text-gray-400">· {((d.amount / total) * 100).toFixed(1)}%</span>
                </span>
              </div>
              {sortedData.length > 6 && <div className="h-2 w-full overflow-hidden rounded-full bg-gray-50" aria-hidden="true">
                <div className="h-full rounded-full" style={{ width: `${(d.amount / sortedData[0].amount) * 100}%`, backgroundColor: d.color }} />
              </div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
